/**
 * @file: WiredChatConversationHeader.tsx
 *
 * Wires `ChatConversationHeader` to the room: it derives the title from the member
 * count (single source of truth `members$`) and subscribes to the typing
 * observable to fold "正在输入…" under the name. Used by the conversation when
 * it owns its header (the desktop pane); on mobile the navigator owns the
 * header and `TypingIndicator` drives it instead.
 */

import { useConversationPeerPresence } from '@euler/app/flows/chat/lib/conversation/hooks/useConversationPeerPresence';
import { useTypingIndicator } from '@euler/app/flows/chat/lib/conversation/hooks/useTypingIndicator';
import { composeConversationTitle } from '@euler/app/flows/chat/utils/conversationTitle';
import { useBehaviorSubject, useLocalObservable } from '@euler/utils/hooks';
import { FC, memo } from 'react';
import { useChatConversation } from '../../lib/conversation/context';
import { ChatConversationHeader } from './ChatConversationHeader';

export const WiredChatConversationHeader: FC<{
  onOpenRoomInfo?: () => void;
}> = memo(({ onOpenRoomInfo }) => {
  const { conversationHandle, members$ } = useChatConversation();
  const [members] = useBehaviorSubject(members$);
  const typing$ = useTypingIndicator();
  const typingNames = useLocalObservable(() => typing$, [], [typing$]);
  const { title, subtitle } = composeConversationTitle(
    conversationHandle,
    members.length,
    typingNames,
  );
  // Private chat: green dot beside the name when online + 在线/离线 under it; a
  // typing indicator wins the subtitle.
  const peerPresence = useConversationPeerPresence();
  const presenceText =
    peerPresence === 'online'
      ? '在线'
      : peerPresence === 'offline'
      ? '离线'
      : '';
  return (
    <ChatConversationHeader
      title={title}
      status={subtitle || presenceText}
      online={peerPresence === 'online'}
      onOpenRoomInfo={onOpenRoomInfo}
    />
  );
});
